import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import doctorApi from '../api/doctor/doctorApi';
import { DAY_PERIODS } from '../utils/constants';
import { addMonths, fromDateKey, toDateKey, today, timeToMinutes } from '../utils/date';

const periodOf = (time) => {
  const m = timeToMinutes(time);
  return DAY_PERIODS.find((p) => m >= timeToMinutes(p.from) && m < timeToMinutes(p.to))?.key;
};

/**
 * Drives the date + time picker used by booking and rescheduling:
 * earliest-available shortcuts, month calendar availability, slots for the chosen day.
 */
export default function useSlotPicker(doctorId, { excludeAppointmentId, bookingWindowDays = 90 } = {}) {
  const [earliest, setEarliest] = useState({ items: [], loading: true });
  const [month, setMonth] = useState(() => {
    const t = today();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [days, setDays] = useState({ map: {}, loading: true });
  const [selectedDate, setSelectedDate] = useState(null);
  const selectedRef = useRef(null);
  selectedRef.current = selectedDate;
  const [slots, setSlots] = useState({ items: [], loading: false, date: null });
  const [period, setPeriod] = useState(DAY_PERIODS[0].key);
  const [selectedTime, setSelectedTime] = useState(null);

  const minMonth = useMemo(() => {
    const t = today();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  }, []);
  const maxMonth = useMemo(() => {
    const t = today();
    t.setDate(t.getDate() + bookingWindowDays);
    return new Date(t.getFullYear(), t.getMonth(), 1);
  }, [bookingWindowDays]);

  // Earliest available days -> also preselect the first one and jump the calendar to it.
  useEffect(() => {
    if (!doctorId) return;
    let alive = true;
    setEarliest({ items: [], loading: true });
    doctorApi.earliest(doctorId, 3).then((items) => {
      if (!alive) return;
      setEarliest({ items, loading: false });
      if (items[0] && !selectedRef.current) {
        const d = fromDateKey(items[0].date);
        setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
        setSelectedDate(items[0].date);
      }
    }).catch(() => alive && setEarliest({ items: [], loading: false }));
    // eslint-disable-next-line consistent-return
    return () => {
      alive = false;
    };
  }, [doctorId]);

  // Day availability for the visible month.
  useEffect(() => {
    if (!doctorId) return;
    let alive = true;
    const from = toDateKey(month);
    const to = toDateKey(new Date(month.getFullYear(), month.getMonth() + 1, 0));
    setDays((d) => ({ ...d, loading: true }));
    doctorApi.availability(doctorId, { from, to, exclude: excludeAppointmentId }).then((list) => {
      if (!alive) return;
      setDays({ map: Object.fromEntries(list.map((x) => [x.date, x])), loading: false });
    }).catch(() => alive && setDays({ map: {}, loading: false }));
    // eslint-disable-next-line consistent-return
    return () => {
      alive = false;
    };
  }, [doctorId, month, excludeAppointmentId]);

  // Slots for the selected day.
  useEffect(() => {
    if (!doctorId || !selectedDate) return;
    let alive = true;
    setSelectedTime(null);
    setSlots({ items: [], loading: true, date: null });
    doctorApi.slots(doctorId, selectedDate, { exclude: excludeAppointmentId }).then((items) => {
      if (!alive) return;
      setSlots({ items, loading: false, date: selectedDate });
      const firstOpen = items.find((s) => s.available);
      if (firstOpen) setPeriod(periodOf(firstOpen.time));
    }).catch(() => alive && setSlots({ items: [], loading: false, date: selectedDate }));
    // eslint-disable-next-line consistent-return
    return () => {
      alive = false;
    };
  }, [doctorId, selectedDate, excludeAppointmentId]);

  const selectDate = useCallback((key) => {
    setSelectedDate(key);
    const d = fromDateKey(key);
    setMonth((m) => (m.getMonth() === d.getMonth() && m.getFullYear() === d.getFullYear()
      ? m : new Date(d.getFullYear(), d.getMonth(), 1)));
  }, []);

  const periods = useMemo(() => DAY_PERIODS.map((p) => {
    const inPeriod = slots.items.filter((s) => periodOf(s.time) === p.key);
    return { ...p, slots: inPeriod, open: inPeriod.filter((s) => s.available).length };
  }), [slots.items]);

  const totalOpen = periods.reduce((n, p) => n + p.open, 0);

  return {
    earliest,
    month,
    canPrevMonth: month > minMonth,
    canNextMonth: month < maxMonth,
    prevMonth: () => setMonth((m) => addMonths(m, -1)),
    nextMonth: () => setMonth((m) => addMonths(m, 1)),
    days,
    selectedDate,
    selectDate,
    slots,
    periods,
    totalOpen,
    period,
    setPeriod,
    selectedTime,
    setSelectedTime,
  };
}
